import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [enableTrialPeriod, setEnableTrialPeriod] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      logger.debug('AuthContext: Setting Authorization header with token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      logger.debug('AuthContext: Removing Authorization header');
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_ENDPOINTS.DRIVERS}/profile`);
          setUser(response.data.driver);
          setSubscription(response.data.subscription || null);
          if (response.data.enableTrialPeriod !== undefined) {
            setEnableTrialPeriod(response.data.enableTrialPeriod);
          }
        } catch (error) {
          logger.error('Auth check failed:', error);
          // Don't logout on subscription errors, just log them
          if (error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
            logger.warn('Subscription required during auth check');
          } else {
            logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_ENDPOINTS.AUTH}/login`, {
        email,
        password
      });

      const { data } = response.data;
      const { token: newToken, driver, subscription: sub } = data;
      
      // Set axios header synchronously before state updates to prevent race conditions
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      localStorage.setItem('token', newToken);
      
      // Update state (this will trigger useEffect to keep header in sync)
      setToken(newToken);
      setUser(driver);
      setSubscription(sub || null);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'שגיאה בהתחברות'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_ENDPOINTS.AUTH}/register`, userData);
      const { data } = response.data;
      // Do not log in yet; just return notice and subscription summary
      return { success: true, data: response.data, subscription: data?.subscription };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'שגיאה בהרשמה'
      };
    }
  };

  const verifyPhone = async (phone, code) => {
    try {
      // Verify and persist on the server (sets driver.phoneVerified = true)
      const response = await axios.post(`${API_ENDPOINTS.AUTH}/verify-phone`, {
        phone,
        code
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'שגיאה באימות מספר הטלפון'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_ENDPOINTS.DRIVERS}/profile`, profileData);
      setUser(response.data.driver);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'שגיאה בעדכון הפרופיל'
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    subscription,
    enableTrialPeriod,
    login,
    register,
    verifyPhone,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
